import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as DoneIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../PageLayout';
import { getAdviseTaskSubject, getAdviseTaskLoadingSubject, useAdviseTasks } from '../../../store/AdviseTask';
import { getAdviseEngagementSubject, useAdviseEngagements } from '../../../store/AdviseEngagement';
import { AdviseTask, AdviseEngagement } from '../../../types/advise';
import { getDate, isPastDue } from '../../../utils/helpers';
import { getSelectedAccountsSubject, hasAdviseFull } from '../../../store/User';
import { createAdviseTask, updateAdviseTask, deleteAdviseTask } from '../../../Api/Advise/Tasks';
import { TaskFormDialog } from '../../Compounds/Advise/TaskFormDialog';
import { ConfirmDialog } from '../../Compounds/Advise/ConfirmDialog';
import { AdviseSubNav } from '../../Compounds/Advise/AdviseSubNav';
import { AdviseAccountGuard } from '../../Compounds/Advise/AdviseAccountGuard';
import { useSnackbar } from '../../../Context/SnackbarContext';

const STATUS_COLUMNS = [
  { key: 'todo', label: 'To Do', color: '#94a3b8' },
  { key: 'in_progress', label: 'In Progress', color: '#3b82f6' },
  { key: 'done', label: 'Done', color: '#22c55e' }
] as const;

const PRIORITY_COLORS: Record<string, 'error' | 'warning' | 'info'> = {
  high: 'error',
  medium: 'warning',
  low: 'info'
};

export const AdviseTasksPage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<AdviseTask[]>([]);
  const [engagements, setEngagements] = useState<AdviseEngagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AdviseTask | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdviseTask | null>(null);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const { fetchTasks } = useAdviseTasks();
  const { setSnack } = useSnackbar();
  const notifyError = useCallback(
    (message?: string) =>
      setSnack({ message: message || 'Something went wrong. Please try again.', open: true, type: 'error' }),
    [setSnack]
  );
  const { fetchEngagements } = useAdviseEngagements();
  const canEdit = hasAdviseFull();

  useEffect(() => {
    const subs = [
      getAdviseTaskSubject().subscribe(setTasks),
      getAdviseTaskLoadingSubject().subscribe(setLoading),
      getAdviseEngagementSubject().subscribe(setEngagements)
    ];
    return () => subs.forEach((s) => s.unsubscribe());
  }, []);

  useEffect(() => {
    fetchTasks(undefined, true);
    fetchEngagements();

    const accountSub = getSelectedAccountsSubject().subscribe(() => {
      fetchTasks(undefined, true);
      fetchEngagements(true);
    });
    return () => accountSub.unsubscribe();
  }, []);

  const filtered = useMemo(() => {
    const today = new Date();
    return tasks
      .filter((t) => {
        if (priorityFilter !== 'all' && t.priority !== priorityFilter) {
          return false;
        }
        if (search) {
          const q = search.toLowerCase();
          return t.title?.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q);
        }
        return true;
      })
      .map((t) => {
        const due = t.due_date ? getDate(t.due_date) : null;
        const pastDue = isPastDue(t.due_date, today);
        const dueToday = !!due && due.toDateString() === today.toDateString();
        return { ...t, _pastDue: pastDue, _dueToday: dueToday };
      })
      .sort((a, b) => {
        const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        return da - db;
      });
  }, [tasks, search, priorityFilter]);

  const getColumnTasks = useCallback((status: string) => filtered.filter((t) => t.status === status), [filtered]);

  const handleCreate = useCallback(
    async (data: Partial<AdviseTask>) => {
      const result = await createAdviseTask(data);
      if (result.error) return notifyError(result.error.message);
      if (result.data) setTasks((prev) => [...prev, result.data!]);
      setShowForm(false);
    },
    [notifyError]
  );

  const handleUpdate = useCallback(
    async (data: Partial<AdviseTask>) => {
      if (!editing) return;
      const result = await updateAdviseTask(editing.id, data);
      if (result.error) return notifyError(result.error.message);
      if (result.data) setTasks((prev) => prev.map((t) => (t.id === editing.id ? result.data! : t)));
      setShowForm(false);
      setEditing(null);
    },
    [editing, notifyError]
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const { error } = await deleteAdviseTask(deleteTarget.id);
    if (error) return notifyError(error.message);
    setTasks((prev) => prev.filter((t) => t.id !== deleteTarget.id));
    setDeleteTarget(null);
  }, [deleteTarget, notifyError]);

  const handleMarkDone = useCallback(
    async (task: AdviseTask) => {
      const result = await updateAdviseTask(task.id, { status: 'done' });
      if (result.error) return notifyError(result.error.message);
      if (result.data) setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: 'done' } : t)));
    },
    [notifyError]
  );

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) return;
      const newStatus = result.destination.droppableId;
      const taskId = result.draggableId;
      const task = tasks.find((t) => t.id === taskId);
      if (!task || task.status === newStatus) return;
      const previousStatus = task.status;

      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t)));
      const { error } = await updateAdviseTask(taskId, { status: newStatus });
      if (error) {
        setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: previousStatus } : t)));
        notifyError(error.message);
      }
    },
    [tasks, notifyError]
  );

  const getEngagementName = useCallback(
    (id?: string) => {
      if (!id) return '';
      return engagements.find((c) => c.id === id)?.engagement_name || '';
    },
    [engagements]
  );

  const pageActions = canEdit ? (
    <Button
      variant="contained"
      size="small"
      startIcon={<AddIcon />}
      onClick={() => {
        setEditing(null);
        setShowForm(true);
      }}>
      New Task
    </Button>
  ) : undefined;

  if (loading && tasks.length === 0) {
    return (
      <PageLayout
        pageTitle="Advise Tasks"
        dataCy="advise-tasks-page">
        <Box className="flex justify-center p-12">
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle="Advise Tasks"
      pageActions={pageActions}
      dataCy="advise-tasks-page">
      <AdviseSubNav />
      <AdviseAccountGuard>
        {/* Filters */}
        <Box className="flex items-center gap-3 mb-4">
          <TextField
            size="small"
            placeholder="Search tasks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 250 }}
          />
          <FormControl
            size="small"
            sx={{ minWidth: 140 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priorityFilter}
              label="Priority"
              onChange={(e) => setPriorityFilter(e.target.value)}>
              <MenuItem value="all">All Priorities</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>
          <Typography
            variant="body2"
            color="textSecondary"
            className="ml-auto">
            {filtered.length} task{filtered.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {/* Kanban Board */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Box className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {STATUS_COLUMNS.map((col) => (
              <Box
                key={col.key}
                className="flex flex-col">
                <Box className="flex items-center gap-2 mb-2 px-2">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: col.color
                    }}
                  />
                  <Typography variant="subtitle2">{col.label}</Typography>
                  <Chip
                    label={getColumnTasks(col.key).length}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Droppable droppableId={col.key}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="flex flex-col gap-2 min-h-[200px] p-2 rounded"
                      sx={{
                        bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'grey.50',
                        transition: 'background-color 0.2s'
                      }}>
                      {getColumnTasks(col.key).map((task, index) => (
                        <Draggable
                          key={task.id}
                          draggableId={task.id}
                          index={index}
                          isDragDisabled={!canEdit}>
                          {(dragProvided) => (
                            <Card
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              variant="outlined"
                              sx={{ cursor: canEdit ? 'grab' : 'default' }}>
                              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Box className="flex items-start justify-between">
                                  <Box className="min-w-0 flex-1">
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}>
                                      {task.title}
                                    </Typography>
                                    {task.description && (
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        className="line-clamp-2"
                                        display="block">
                                        {task.description}
                                      </Typography>
                                    )}
                                    <Box className="flex items-center gap-1 mt-1 flex-wrap">
                                      <Chip
                                        label={task.priority}
                                        size="small"
                                        color={PRIORITY_COLORS[task.priority] || 'default'}
                                        variant="outlined"
                                      />
                                      {task.due_date && (
                                        <Chip
                                          label={getDate(task.due_date).toLocaleDateString()}
                                          size="small"
                                          color={
                                            (task as { _pastDue?: boolean })._pastDue
                                              ? 'error'
                                              : (task as { _dueToday?: boolean })._dueToday
                                                ? 'warning'
                                                : 'default'
                                          }
                                          variant="outlined"
                                        />
                                      )}
                                      {task.engagement_id && (
                                        <Chip
                                          icon={<LinkIcon />}
                                          label={getEngagementName(task.engagement_id) || 'Engagement'}
                                          size="small"
                                          variant="outlined"
                                          onClick={() => navigate(`/advise/analysis?id=${task.engagement_id}`)}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                  {canEdit && (
                                    <Box className="flex flex-col ml-1">
                                      {task.status !== 'done' && (
                                        <IconButton
                                          size="small"
                                          color="success"
                                          title="Mark done"
                                          onClick={() => handleMarkDone(task)}>
                                          <DoneIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                      <IconButton
                                        size="small"
                                        title="Edit"
                                        onClick={() => {
                                          setEditing(task);
                                          setShowForm(true);
                                        }}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        title="Delete"
                                        onClick={() => setDeleteTarget(task)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {getColumnTasks(col.key).length === 0 && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className="text-center py-8"
                          display="block">
                          No tasks
                        </Typography>
                      )}
                    </Box>
                  )}
                </Droppable>
              </Box>
            ))}
          </Box>
        </DragDropContext>

        {/* Task Form Dialog */}
        <TaskFormDialog
          open={showForm}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSubmit={editing ? handleUpdate : handleCreate}
          task={editing}
          engagements={engagements}
        />

        {/* Delete Confirm */}
        <ConfirmDialog
          open={!!deleteTarget}
          title="Delete Task"
          message={`Delete "${deleteTarget?.title}"?`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      </AdviseAccountGuard>
    </PageLayout>
  );
};
